import axios, { AxiosError } from "axios";
import _ from "lodash";
import { Logger } from "../logging/logger";
import { GalleryConfigType, GalleryContentType } from "../types/GalleryContentServiceTypes";
import PageConfig from "../utils/PageConfig";
const BASE_URL = `https://gallery-content-query.cdn.vpsvc.com`;

export const getGalleryConfig = async (
  mpvId: string,
  locale: string,
  auth: any
): Promise<GalleryConfigType | undefined> => {
  const url = `https://gallery-content-api.cdn.vpsvc.com/api/v4/ProductPage/${mpvId}/Config/Culture/${locale.toLowerCase()}`;
  const authorizationHeader = auth.getAuthorizationHeader();

  if (authorizationHeader) {
    try {
      const params = {
        requestor: PageConfig.requestor,
      };
      const config = {
        params,
        headers: {
          authorization: authorizationHeader,
        },
      };
      const response = await axios.get(url, config);
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      const errorMsg = "Failed to get gallery config for product.";
      logGalleryError(e as AxiosError, url, errorMsg);
    }
  }

  return undefined;
};

export const getGalleryTemplates = async (
  version: number,
  configuration: Record<string, string>,
  galleryName: string,
  locale: string,
  auth: any
): Promise<GalleryContentType | undefined> => {
  const baseUrl = `${BASE_URL}/api/v2/Galleries/${galleryName}/Culture/${locale}/Content`;
  const authorizationHeader = auth.getAuthorizationHeader();
  if (authorizationHeader) {
    try {
      const limit = 1;

      const optionSelections = Object.entries(configuration)
        .map(
          entry =>
            encodeURIComponent(`selectedOptions[${entry[0]}]`) + "=" + encodeURIComponent(entry[1])
        )
        .join("&");

      const url = `${baseUrl}?limit=${limit}&requestor=${PageConfig.requestor}&productVersion=${version}&${optionSelections}`;
      const config = {
        headers: {
          authorization: authorizationHeader,
        },
      };
      const response = await axios.get(url, config);
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      const errorMsg = "Failed to get gallery templates for product.";
      logGalleryError(e as AxiosError, baseUrl, errorMsg);
    }
  }
  return undefined;
};
export const getDesignsOptions = async (
  version: number,
  galleryName: string,
  locale: string,
  auth: any,
  designId?: string
): Promise<GalleryContentType | undefined> => {
  const baseUrl = `${BASE_URL}/api/v2/Galleries/${galleryName}/Culture/${locale}/Content`;
  const authorizationHeader = auth.getAuthorizationHeader();
  if (authorizationHeader) {
    try {
      const limit = 1;
      const url =
        `${baseUrl}?limit=${limit}&requestor=${PageConfig.requestor}&productVersion=${version}` +
        (designId ? `&designIds=${designId}` : ``);
      const config = {
        headers: {
          authorization: authorizationHeader,
        },
      };
      const response = await axios.get(url, config);
      if (response.data) {
        return response.data;
      }
    } catch (e) {
      const errorMsg = "Failed to get gallery template for designId.";
      logGalleryError(e as AxiosError, baseUrl, errorMsg);
    }
  }
  return undefined;
};

export const getMatchingTemplates = async (
  productKey: string,
  version: number,
  templatedId: string,
  locale: string,
  selections: Record<string, string>,
  requestor: string
): Promise<MatchingTemplateUrls[] | undefined> => {
  const baseUrl = `${BASE_URL}/api/v2/Product/${productKey}/Culture/${locale.toLowerCase()}/matching`;

  try {
    const optionSelections = `&${Object.entries(selections)
      .map(
        entry =>
          encodeURIComponent(`productOptions[${entry[0]}]`) + "=" + encodeURIComponent(entry[1])
      )
      .join("&")}`;

    const url = `${baseUrl}?templateIdentifier=${templatedId}&productVersion=${version}${optionSelections}&requestor=${requestor}`;

    const response = await axios.get(url);
    if (response.data) {
      return response.data;
    }
  } catch (e) {
    const errorMsg = "Failed to get templates urls";
    logGalleryError(e as AxiosError, baseUrl, errorMsg);
  }
  return undefined;
};

export const getContent = async (
  keywords: string,
  mpvId: string,
  gallery: string,
  locale: string,
  selections: Record<string, string>,
  requestor: string
): Promise<ContentResponse | undefined> => {
  const baseUrl = `https://gallery-content-query.cdn.vpsvc.com/api/v2/Galleries/${gallery}/Culture/${locale}/Content`;
  try {
    const optionSelections = `&${Object.entries(selections)
      .map(
        entry =>
          encodeURIComponent(`productOptions[${entry[0]}]`) + "=" + encodeURIComponent(entry[1])
      )
      .join("&")}`;

    const url = `${baseUrl}?keywords=${keywords}&requestor=${requestor}${optionSelections}&requestor=${requestor}&mpvid=${mpvId}`;

    const response = await axios.get<ContentResponse>(url);

    if (response.data) {
      return response.data;
    }
  } catch (e) {
    const errorMsg = "Failed to get content";
    logGalleryError(e as AxiosError, baseUrl, errorMsg);
  }
  return undefined;
};

export const getGalleries = async (
  productKey: string,
  locale: string,
  requestor: string
): Promise<GalleriesResponse | undefined> => {
  const url = `https://gallery-content-api.cdn.vpsvc.com/api/v4/Galleries?productKey=${productKey}&culture=${locale.toLowerCase()}&requestor=${requestor}`;
  try {
    const response = await axios.get<GalleriesResponse>(url);

    if (response.data) {
      return response.data;
    }
  } catch (e) {
    const errorMsg = "Failed to get galleries";
    logGalleryError(e as AxiosError, url, errorMsg);
  }
  return undefined;
};

function logGalleryError(error: AxiosError, url: string, errorMsg: string): void {
  const status = error?.response?.status;
  if (status !== 400) {
    Logger.instance.error(errorMsg, { error, errorMessage: error.message, url, status });
  } else {
    Logger.instance.warning(errorMsg, { error, errorMessage: error.message, url, status });
  }
}

export interface MatchingTemplateUrls {
  designId: string;
  templateId: string;
  templateToken: string;
  designConceptId: string;
  productKey: string;
  productVersion: number;
  fullProductSpecification: Record<string, string>;
  altText: string;
  instructionsUrl: string;
  previewUrl: string;
  studioUrl: string;
  placeholderPurposes: string[];
  placeholderNumber: string;
}

export interface ContentResponse {
  results: ContentResults;
  offset: number;
  length: number;
  total: number;
}

export interface ContentResults {
  content: ContentVariation[];
  facets: Facet[];
  numFound: number;
  debugState: any;
  boostInfo: any;
}

export interface ContentVariation {
  designId: string;
  designconceptId: string;
  altText: string;
  legacyComboId: string;
  comparativeName: string;
  finishFlagTreatment: any;
  ranking: number;
  isForceRanked: boolean;
  studioUrl: string;
  productKey: string;
  productVersion: number;
  previewUrls: PreviewUrls;
  previewInfo: PreviewInfo;
  colorSwatches: ColorSwatch[];
  selectedColorSwatchDesignId: string;
  productOptions: Record<string, string>;
  fullProductOptions: Record<string, string>;
  keywords: any;
  categoryNames: any;
  categoryKeys: any;
  templateUseCases: any[];
  numberOfPlaceholders: string;
  colorComposition: ColorComposition[];
  designColors: DesignColor[];
  designCreationType: string;
  availableOptionsForExpansion: any;
  availableTemplateUseCasesForExpansion: any;
  externalId: string;
  templateUseCase: any;
}

export interface PreviewUrls {
  size1x: string;
  size2x: string;
}

export interface PreviewInfo {
  templateToken: string;
  designId: string;
  dps: string;
  aspectRatio: number;
  finishType: string;
  scenes: Scenes;
  imageCacheVersion: any;
  instructionsUrl: string;
}

export interface Scenes {
  gallery: {
    uri: string;
    aspectRatio: number;
    sceneType: string;
  };
}

export interface ColorSwatch {
  designId: string;
  isMainSwatch: boolean;
  color: string;
  studioUrl: string;
  altText: string;
  colorComposition: ColorComposition[];
  designColors: DesignColor[];
  differentialColors: ColorComposition[];
  previewUrls: PreviewUrls;
  previewInfo: PreviewInfo;
  isLightColor: boolean;
  externalId: string;
}

export interface ColorComposition {
  color: string;
  weight: number;
}

export interface DesignColor {
  rgb: RGB;
  weight: number;
}

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface Facet {
  facetType: string;
  facet: string;
  count: number;
}

export interface GalleriesResponse {
  results: GalleryResult[];
  offset: number;
  length: number;
  total: number;
  limit: number;
}

export interface GalleryResult {
  id: number;
  name: string;
  productKey: string;
  mpv: string;
  isDefault: boolean;
  securityBoundary: string;
  h1Title: string;
  sourceGalleryId: number;
  comparativeNameFormat: any;
  migrationStatus: string;
  galleryNames: Gallery[];
  status: string;
  notificationChannels: Channel[];
  rankingStrategy: string;
  forcedRankingStrategy: string;
}

export interface Gallery {
  name: string;
  galleryId: number;
  culture: string;
  isDefault: boolean;
  buildTlp: string;
}

export interface Channel {
  id: number;
  label: string;
  channelName: any;
  defaultFor: string;
  active: boolean;
}
